
import React from "react";
import { Helmet } from "react-helmet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DashboardLayout from "@/components/DashboardLayout";
import PageCallToAction from "@/components/PageCallToAction";

const Cookies = () => {
  return (
    <DashboardLayout>
      <div className="container mx-auto py-12 px-4">
        <Helmet>
          <title>Cookie Policy | OpenPipe</title>
        </Helmet>
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-3xl font-bold">Cookie Policy</CardTitle>
            <CardDescription>Last updated July 29, 2024</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 legal-page">
            <p className="mb-4">
              This Cookie Policy explains how OpenPipe, Inc. ("Company," "we,"
              "us," and "our") uses cookies and similar technologies to
              recognize you when you visit our website at https://openpipe.ai
              ("Website") and its subdomains. It explains what these
              technologies are and why we use them in connection with our
              Services (as outlined in our Privacy Policy), as well as your
              rights to control our use of them.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              What are cookies?
            </h2>
            <p className="mb-4">
              Cookies are small data files that are placed on your computer or
              mobile device when you visit a website. Website owners widely use
              cookies to improve the performance of their websites and provide
              reporting information.
            </p>
            <p className="mb-4">
              Cookies set by the website owner (in this case, OpenPipe, Inc.)
              are called "first-party cookies." Cookies set by parties other
              than the website owner are called "third-party cookies."
              Third-party cookies enable third-party features or functionality
              to be provided on or through the website (e.g., advertising,
              interactive content, and analytics). The parties that set these
              third-party cookies can recognize your computer both when it
              visits the website in question and also when it visits certain
              other websites.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              Why do we use cookies?
            </h2>
            <p className="mb-4">
              We use first- and third-party cookies for several reasons. Some
              cookies are required for technical reasons for our Website to
              operate, and we refer to these as "essential" or "strictly
              necessary" cookies. Other cookies enable us to track and target
              the interests of our users to enhance the experience on our Online
              Properties. Third parties serve cookies through our Website for
              advertising, analytics, and other purposes. This is described in
              more detail below.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              How can I control cookies?
            </h2>
            <p className="mb-4">
              You have the right to decide whether to accept or reject cookies.
              You can exercise your cookie rights by setting your preferences in
              the Cookie Consent Banner, available only for users from the EU.
              We determine if a user is from the EU based on the user's timezone
              and browser language settings. The Cookie Consent Banner allows
              you to reject non-essential cookies. Essential cookies cannot be
              rejected as they are strictly necessary to provide you with
              services.
            </p>
            <p className="mb-4">
              By clicking the "decline" button, our analytic tools stop using
              cookies for tracking users. If a user does not see the banner but
              still wants to change their preferences, they may manually disable
              cookies in browser settings. If you choose to reject cookies, you
              may still use our website, though your access to some
              functionality and areas of our website may be restricted.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              The specific types of cookies we use
            </h2>
            <p className="mb-4">
              The specific types of first- and third-party cookies served
              through our Website and the purposes they perform are described in
              the tables below:
            </p>

            <h3 className="text-lg font-semibold mt-5 mb-2">
              Analytics cookies:
            </h3>
            <p className="mb-3">
              These cookies assist us in analyzing and understanding the
              performance and usage of our services, including metrics like the
              number of users, their interactions, and the time spent on our
              services. You can disable these cookies if you prefer.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full bg-card text-foreground border border-border dark:border-border">
                <thead>
                  <tr className="bg-muted/50 dark:bg-muted/30">
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">Name</th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">
                      Data processor
                    </th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">
                      Data controller
                    </th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">ph_phc</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">PostHog</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Analytics</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">hubspot</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">HubSpot</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      Customer Support, Marketing
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">hs / _hs</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">HubSpot</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      Customer Support, Marketing
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">csrf.app</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">HubSpot</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      Customer Support, Marketing
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">_conv</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">HubSpot</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      HubSpot through OpenPipe
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      Customer Support, Marketing
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">_hj</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Hotjar</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      HubSpot through OpenPipe
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Analytics</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">_gcl</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Google Ads</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      HubSpot through OpenPipe
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Marketing</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">_gcl</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Google Analytics</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      HubSpot through OpenPipe
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Analytics</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">_cf / cf</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Cloudflare</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      HubSpot through OpenPipe
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Security</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold mt-5 mb-2">
              Necessary cookies:
            </h3>
            <p className="mb-3">
              These cookies are essential for the functioning of our services.
              They enable us to authenticate users and activate specific
              features within our services, including those related to security.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full bg-card text-foreground border border-border dark:border-border">
                <thead>
                  <tr className="bg-muted/50 dark:bg-muted/30">
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">Name</th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">
                      Data processor
                    </th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">
                      Data controller
                    </th>
                    <th className="py-2 px-4 border-b border-border dark:border-border text-left">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">sessionId</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">User authentication</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">
                      next-auth / __Secure-next-auth / __Host-next-auth
                    </td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">User authentication</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border-b border-border dark:border-border">__stripe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Stripe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">OpenPipe</td>
                    <td className="py-2 px-4 border-b border-border dark:border-border">Payment processing</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              How can I control cookies on my browser?
            </h2>
            <p className="mb-4">
              As the means by which you can refuse cookies through your web
              browser controls vary from browser to browser, you should visit
              your browser's help menu for more information. The following is
              information about how to manage cookies on the most popular
              browsers:
            </p>

            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Chrome</li>
              <li className="mb-2">Internet Explorer</li>
              <li className="mb-2">Firefox</li>
              <li className="mb-2">Safari</li>
              <li className="mb-2">Edge</li>
              <li className="mb-2">Opera</li>
            </ul>

            <p className="mb-4">
              In addition, most advertising networks offer you a way to opt out
              of targeted advertising. If you would like to find out more
              information, please visit:
            </p>

            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Digital Advertising Alliance</li>
              <li className="mb-2">Digital Advertising Alliance of Canada</li>
              <li className="mb-2">
                European Interactive Digital Advertising Alliance
              </li>
            </ul>

            <h2 className="text-xl font-semibold mt-6 mb-3">
              Where can I get further information?
            </h2>
            <p className="mb-4">
              To get more information, please email us at{" "}
              <a
                href="mailto:notices@openpipe.ai"
              >
                notices@openpipe.ai
              </a>
              .
            </p>
          </CardContent>
        </Card>

        <div className="mt-12">
          <PageCallToAction />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Cookies;
